import * as RDialog from '@radix-ui/react-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { X } from 'lucide-react';
import { ReactNode, useEffect } from 'react';

export function Dialog({
	title,
	children,
	closable = true,
	onClose,
	zIndex,
	className,
	cerrarButton = true,
	disableOverlayClose = false,
	noContentWrapper = false
}: {
	title: ReactNode;
	children: ReactNode;
	closable?: boolean;
	onClose?: () => any;
	zIndex?: number;
	className?: string;
	cerrarButton?: boolean;
	disableOverlayClose?: boolean;
	noContentWrapper?: boolean;
}) {
	useEffect(() => {
		return () => {
			document.body.style.pointerEvents = '';
		};
	}, []);

	return (
		<RDialog.Root
			defaultOpen={true}
			onOpenChange={(open) => {
				if (!open && onClose) onClose();
			}}
		>
			<RDialog.Portal>
				<RDialog.Overlay
					className="fixed inset-0 z-40 bg-black/70 backdrop-blur-[1.5px] data-[state=open]:animate-overlayShow"
					style={{ ...(zIndex && { zIndex }) }}
				/>
				<RDialog.Content
					className={`fixed left-[50%] top-[50%] z-50 flex max-h-[90vh] w-max max-w-[min(800px,90vw)] translate-x-[-50%] translate-y-[-50%] flex-col gap-4 rounded-xl border-[2px] border-neutral-900/65 bg-background p-6 text-neutral-100 focus:outline-none data-[state=open]:animate-contentShow light:border-none light:bg-white light:shadow-xl ${className}`}
					style={{ ...(zIndex && { zIndex: zIndex + 1 }) }}
					onOpenAutoFocus={(e) => e.preventDefault()}
					onEscapeKeyDown={(e) => !closable && e.preventDefault()}
					onInteractOutside={(e) => (!closable || disableOverlayClose) && e.preventDefault()}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							e.preventDefault();
							e.stopPropagation();

							// Find the submit button and click it
							const submitButton = e.currentTarget.querySelector('button[type="submit"]');
							if (submitButton && typeof submitButton === 'object' && 'click' in submitButton) {
								(submitButton as HTMLButtonElement).click();
							}
						}
					}}
				>
					{title ? (
						<RDialog.Title className="select-none text-[1.1rem] font-medium">{title}</RDialog.Title>
					) : (
						<VisuallyHidden.Root asChild>
							<RDialog.Title>Dialog</RDialog.Title>
						</VisuallyHidden.Root>
					)}
					<RDialog.Description className="hidden"></RDialog.Description>

					{noContentWrapper ? (
						children
					) : (
						<div className="overflow-y-auto p-1 leading-normal text-neutral-200">{children}</div>
					)}

					{closable && cerrarButton && (
						<>
							<div className="flex justify-end">
								<RDialog.Close asChild>
									<button className="inline-flex h-[35px] items-center justify-center rounded-lg px-[15px] font-medium leading-none text-neutral-600 transition-colors hover:bg-neutral-900/50 focus:outline-none disabled:pointer-events-none disabled:opacity-50">
										Cerrar
									</button>
								</RDialog.Close>
							</div>
						</>
					)}
					{closable && (
						<>
							<RDialog.Close asChild>
								<button
									className="absolute right-[3px] top-[3px] inline-flex h-[25px] w-[25px] appearance-none items-center justify-center rounded-full text-neutral-300 hover:bg-neutral-900/50 focus:outline-none"
									aria-label="Close"
								>
									<X className="size-4" strokeWidth={2.5} />
								</button>
							</RDialog.Close>
						</>
					)}
				</RDialog.Content>
			</RDialog.Portal>
		</RDialog.Root>
	);
}
